import React, { useState, useMemo } from 'react';

import Modal from 'components/partials/modal/modal';
import Button from 'components/partials/button/button';
import Input from 'components/partials/input/input';
import Select from 'components/partials/select/select';

type ModalActionType =
  | 'rename-group'
  | 'duplicate-group'
  | 'remove-group'
  | 'rename-regime'
  | 'duplicate-regime'
  | 'move-regime'
  | 'remove-regime';

interface ModalConfig {
  headerText: string;
  confirmLabel: string;
  hasInput?: boolean;
  inputLabel?: string;
  hasSelect?: boolean;
  selectLabel?: string;
  isRemove?: boolean;
  warning?: string;
}

const MODAL_CONFIG: Record<ModalActionType, ModalConfig> = {
  'rename-group': {
    headerText: 'Rename group of regimes',
    confirmLabel: 'Save',
    hasInput: true,
    inputLabel: 'Group name',
  },
  'duplicate-group': {
    headerText: 'Duplicate group of regimes',
    confirmLabel: 'Save',
    hasInput: true,
    inputLabel: 'Group name',
  },
  'remove-group': {
    headerText: 'Remove group of regimes',
    confirmLabel: 'Remove',
    isRemove: true,
    warning: `All regimes included into this group will be removed.\nAre you sure? This can't be undone`,
  },
  'rename-regime': {
    headerText: 'Rename regime',
    confirmLabel: 'Save',
    hasInput: true,
    inputLabel: 'Regime name',
  },
  'duplicate-regime': {
    headerText: 'Duplicate regime',
    confirmLabel: 'Save',
    hasInput: true,
    inputLabel: 'Regime name',
  },
  'move-regime': {
    headerText: 'Move regime into the group',
    confirmLabel: 'Submit',
    hasSelect: true,
    selectLabel: 'Group name',
  },
  'remove-regime': {
    headerText: 'Remove regime',
    confirmLabel: 'Remove',
    isRemove: true,
    warning: `The regime will be removed. Are you sure?\nThis can't be undone`,
  },
};

interface ModalEntity {
  id?: string | number;
  name?: string;
  groupId?: string | number | null;
}

interface GroupItem {
  id: string | number;
  name: string;
}

interface RegimeActionModalProps {
  type: string;
  entity?: ModalEntity | null;
  groups?: GroupItem[];
  onClose: () => void;
  onConfirm: (arg?: string | number | null) => void;
  loading?: boolean;
}

const getInitialValue = (
  type: ModalActionType,
  entity?: ModalEntity | null
): string => {
  if (type === 'rename-group' || type === 'rename-regime') {
    return entity?.name ?? '';
  }

  if (type === 'duplicate-group' || type === 'duplicate-regime') {
    return entity?.name ? `${entity.name} copy` : '';
  }

  return '';
};

const RegimeActionModal = ({
  type,
  entity,
  groups = [],
  onClose,
  onConfirm,
  loading = false,
}: RegimeActionModalProps): React.JSX.Element => {
  const config =
    MODAL_CONFIG[type as ModalActionType] ?? MODAL_CONFIG['rename-group'];

  const [inputValue, setInputValue] = useState(() =>
    getInitialValue(type as ModalActionType, entity)
  );
  const [selectedGroupId, setSelectedGroupId] = useState<
    string | number | null
  >(null);

  const groupOptions = useMemo(
    () =>
      groups
        .filter((g) => g.id !== entity?.groupId)
        .map((g) => ({ value: g.id, label: g.name })),
    [groups, entity?.groupId]
  );

  const isConfirmDisabled = useMemo(() => {
    if (config.hasInput) return !inputValue.trim();
    if (config.hasSelect) return !selectedGroupId;

    return false;
  }, [config, inputValue, selectedGroupId]);

  const handleConfirm = () => {
    if (config.hasInput) {
      onConfirm(inputValue.trim());
    } else if (config.hasSelect) {
      onConfirm(selectedGroupId);
    } else {
      onConfirm();
    }
  };

  const footer = (
    <div className="flex items-center gap-4">
      <Button
        type="button"
        variant={config.isRemove ? 'critical-filled' : 'brand-filled'}
        size="md"
        disabled={isConfirmDisabled}
        loading={loading}
        onClick={handleConfirm}
        data-testid="confirm-action-button"
      >
        {config.confirmLabel}
      </Button>
      <Button
        type="button"
        variant="reserve-text"
        size="md"
        onClick={onClose}
        data-testid="cancel-action-button"
      >
        Cancel
      </Button>
    </div>
  );

  return (
    <Modal
      headerText={config.headerText}
      onCloseCallback={onClose}
      showCloseButton
      disableBackdropClick
      footer={footer}
      skipUnmountOnClose
      modalClassName="!w-[452px]"
      data-testid="regime-action-modal"
    >
      <div className="mt-4 space-y-4">
        {config.hasInput && (
          <Input
            label={config.inputLabel}
            labelInTop
            placeholder="Type..."
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            data-testid="action-modal-input"
          />
        )}

        {config.hasSelect && (
          <Select
            label={config.selectLabel}
            labelInTop
            options={groupOptions}
            placeholder="Select group..."
            value={
              groupOptions.find((o) => o.value === selectedGroupId) ?? undefined
            }
            onChange={(option) => {
              if (option && !Array.isArray(option)) {
                setSelectedGroupId(option.value);
              } else {
                setSelectedGroupId(null);
              }
            }}
            menuPortalTarget={document.body}
            extraStyles={() => ({
              menuPortal: (base: Record<string, unknown>) => ({ ...base, zIndex: 800 }),
            })}
            data-testid="action-modal-select"
          />
        )}

        {config.warning && (
          <p className="text-sm text-gray-600 whitespace-pre-line">
            {config.warning}
          </p>
        )}
      </div>
    </Modal>
  );
};

export default RegimeActionModal;
